import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Camera, Upload, X } from "lucide-react";
import { OrderTimeline } from "@/components/OrderBits";

const FORWARD = [
  { key: "ordered", label: "Ordered" },
  { key: "admin_verified", label: "Confirmed" },
  { key: "dispatched", label: "Shipped" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
];
// Map every forward micro-status onto a step of the buyer-facing bar.
const FWD_INDEX = {
  ordered: 0,
  admin_verified: 1, packing: 1, ready_to_dispatch: 1,
  dispatched: 2, in_transit: 2,
  out_for_delivery: 3,
  delivered: 4, completed: 4,
};
const RETURN_LINE = [
  { key: "return_requested", label: "Requested" },
  { key: "return_shipped", label: "You Shipped" },
  { key: "return_received", label: "Vendor Received" },
  { key: "return_vendor_approved", label: "Vendor Approved" },
  { key: "refunded", label: "Refunded" },
];

const CREDIT_END = "credit_issued";
const REFUND_END = "refunded";

function statusLabel(s) { return s.replace(/_/g, " "); }

function Tracker({ order }) {
  const status = order.status;
  const [showHistory, setShowHistory] = useState(false);
  const isReturn = status.startsWith("return_") || status === "refunded" || status === "credit_issued";
  const isRto = status === "rto_in_transit" || status === "rto_delivered";
  const line = isReturn ? RETURN_LINE : FORWARD;
  // For credit issued, replace last step
  const activeLine = isReturn && status === CREDIT_END
    ? line.map((s) => s.key === REFUND_END ? { key: CREDIT_END, label: "Credit Issued" } : s)
    : line;
  let active;
  if (isReturn) {
    const idx = activeLine.findIndex((s) => s.key === status);
    active = idx === -1 ? activeLine.length - 1 : idx;
  } else {
    active = FWD_INDEX[status] ?? -1;
  }
  const done = status === "delivered" || status === "completed";

  return (
    <div>
      {!isRto && (
        <div className="grid gap-1 mt-3" style={{ gridTemplateColumns: `repeat(${activeLine.length}, 1fr)` }}>
          {activeLine.map((s, i) => (
            <div key={s.key}>
              <div className={`h-2 ${i <= active ? (isReturn ? "bg-destructive" : "bg-[hsl(var(--accent))]") : "bg-secondary"}`} />
              <p className={`mt-2 font-mono text-[9px] uppercase tracking-widest ${i <= active ? "" : "text-muted-foreground"}`}>{s.label}</p>
            </div>
          ))}
        </div>
      )}
      {status === "completed" && <p className="mt-3 text-xs font-mono text-[hsl(var(--accent))]">Order completed — thanks for shopping with The Spotters</p>}
      {status === "in_transit" && <p className="mt-3 text-xs font-mono text-muted-foreground">On its way{order.awb ? ` · AWB ${order.awb}` : ""}</p>}
      {status === "out_for_delivery" && <p className="mt-3 text-xs font-mono text-[hsl(var(--accent))]">Out for delivery today</p>}
      {isRto && <p className="mt-3 text-xs font-mono text-destructive">Undeliverable — the parcel is returning to the seller. Your refund will follow automatically.</p>}
      {status === "return_vendor_rejected" && <p className="mt-3 text-xs font-mono text-destructive">Vendor rejected — escalated to Spotters Hub for final call</p>}
      {status === "return_disputed" && <p className="mt-3 text-xs font-mono text-destructive">Vendor raised a dispute — under admin review</p>}
      {status === "return_admin_rejected" && <p className="mt-3 text-xs font-mono text-destructive">Return rejected by Spotters Hub (final decision)</p>}
      {status === "return_admin_approved" && <p className="mt-3 text-xs font-mono text-[hsl(var(--accent))]">Return approved by Spotters Hub — awaiting refund</p>}
      {status === "cancelled" && <p className="mt-3 text-xs font-mono text-destructive">Order cancelled — refund processed</p>}
      {status === "pending_payment" && <p className="mt-3 text-xs font-mono text-destructive">Payment pending</p>}

      {order.timeline?.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowHistory((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
            {showHistory ? "Hide tracking history" : "Tracking history"}
          </button>
          {showHistory && <div className="mt-2"><OrderTimeline timeline={order.timeline} /></div>}
        </div>
      )}
    </div>
  );
}

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function ReturnDialog({ order, open, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [images, setImages] = useState([]);
  const [pref, setPref] = useState("original_payment");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const b64s = await Promise.all(files.map(toBase64));
    setImages((s) => [...s, ...b64s]);
    e.target.value = "";
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please share a reason"); return; }
    if (images.length === 0) { toast.error("Attach at least one image of the product"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/return-request`, {
        reason, images, refund_preference: pref,
      });
      toast.success("Return request registered — you'll see status updates here");
      onSubmitted(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Return Request · Order № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">Send it back.</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reason</label>
            <textarea
              data-testid="return-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Sizing came in tight / stitching defect on left shoe / not as described..."
              className="underline-input min-h-[80px] w-full"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Attach photos of the product *</label>
            <p className="font-mono text-[10px] text-muted-foreground mt-1">At least one image required. Take fresh photos or upload from device.</p>

            <div className="mt-3 grid grid-cols-4 gap-2 mb-3">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-square bg-secondary">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setImages((s) => s.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-foreground text-background w-6 h-6 text-xs"
                    data-testid={`remove-img-${i}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                data-testid="capture-camera-btn"
                onClick={() => cameraRef.current?.click()}
                className="flex-1 border border-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background flex items-center justify-center gap-2"
              >
                <Camera size={14} /> Take Photo
              </button>
              <button
                type="button"
                data-testid="upload-file-btn"
                onClick={() => fileRef.current?.click()}
                className="flex-1 border border-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background flex items-center justify-center gap-2"
              >
                <Upload size={14} /> Upload Files
              </button>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={addFiles} className="hidden" />
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={addFiles} className="hidden" />
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">If approved, refund me via</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "original_payment", n: "Original Payment", d: "Refunded via Razorpay to your source. 5-7 working days." },
                { v: "store_credit", n: "Store Credit", d: "Instant on approval. Use on your next order at TheSpotters." },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`pref-${o.v}`}
                  onClick={() => setPref(o.v)}
                  className={`text-left p-4 border transition-colors ${pref === o.v ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}
                >
                  <p className="font-serif text-lg">{o.n}</p>
                  <p className={`font-mono text-[10px] mt-1 ${pref === o.v ? "text-background/70" : "text-muted-foreground"}`}>{o.d}</p>
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="submit-return"
            disabled={busy}
            onClick={submit}
            className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] py-3 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground hover:text-background disabled:opacity-50"
          >
            {busy ? "Registering..." : "Submit Return Request"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BuyerDashboard() {
  const [orders, setOrders] = useState([]);
  const [returning, setReturning] = useState(null);
  const [paying, setPaying] = useState(null);
  const { user, setUser } = useAuth();

  const load = async () => {
    const [o, me] = await Promise.all([
      api.get("/orders").then(r => r.data),
      api.get("/auth/me").then(r => r.data).catch(() => null),
    ]);
    setOrders(o);
    if (me) setUser(me);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markShipped = async (id) => {
    await api.post(`/orders/${id}/return-shipped`);
    toast.success("Marked as shipped — vendor will confirm on receipt");
    load();
  };

  const completePayment = async (o) => {
    setPaying(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/pay`);
      if (data.razorpay_order_id && data.razorpay_key_id && window.Razorpay) {
        const rz = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount_paise,
          currency: "INR",
          order_id: data.razorpay_order_id,
          name: "TheSpotters",
          description: `Order ${o.id.slice(0, 8)}`,
          prefill: { name: user?.name, email: user?.email },
          theme: { color: "#a13d1e" },
          handler: async (res) => {
            try {
              await api.post("/orders/verify", {
                order_id: o.id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_signature: res.razorpay_signature,
              });
              toast.success("Payment received — order placed");
              await load();
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed. Please try again.");
            } finally {
              setPaying(null);
            }
          },
          modal: { ondismiss: () => setPaying(null) },
        });
        rz.on("payment.failed", () => {
          toast.error("Payment failed. Please try again.");
          setPaying(null);
        });
        rz.open();
      } else {
        await api.post(`/orders/${o.id}/mock-pay`);
        toast.success("Order placed (mock payment)");
        await load();
        setPaying(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start payment. Please try again.");
      setPaying(null);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your Account</p>
          <h1 className="font-serif text-6xl tracking-tighter">Orders.</h1>
        </div>
        <div className="border border-[hsl(var(--accent))] p-4 tag-punch pl-10 min-w-[220px]" data-testid="store-credit-card">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Store Credit</p>
          <p className="font-serif text-3xl text-[hsl(var(--accent))] mt-1">₹ {(user?.store_credit_balance || 0).toLocaleString("en-IN")}</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <p className="font-serif text-2xl text-muted-foreground">No orders yet. <Link to="/shop" className="underline">Head to the shop.</Link></p>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-foreground p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Order № {o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("en-IN")}
                  </p>
                  <p className="font-serif text-2xl mt-1 tracking-tighter">₹ {o.total.toLocaleString("en-IN")}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    Status: <span className={o.status.includes("return") || o.status === "refunded" || o.status === "credit_issued" ? "text-destructive" : "text-foreground"}>{statusLabel(o.status)}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  {o.status === "pending_payment" && (
                    <button data-testid={`pay-btn-${o.id}`} onClick={() => completePayment(o)} disabled={paying === o.id}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
                      {paying === o.id ? "Processing…" : "Complete Payment"}
                    </button>
                  )}
                  {o.status === "delivered" && (
                    <button data-testid={`return-btn-${o.id}`} onClick={() => setReturning(o)}
                      className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">
                      Request Return
                    </button>
                  )}
                  {o.status === "return_requested" && (
                    <button data-testid={`shipped-btn-${o.id}`} onClick={() => markShipped(o.id)}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))]">
                      I've Shipped It Back
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-6 grid gap-2">
                {o.items.map((it, i) => (
                  <div key={`${o.id}-${it.product_id}-${it.size}-${i}`} className="grid grid-cols-[60px_1fr_auto] gap-4 items-center">
                    <div className="aspect-square bg-secondary overflow-hidden">
                      {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <p className="font-serif">{it.title}</p>
                      <p className="text-[11px] font-mono text-muted-foreground">Size {it.size} · Qty {it.quantity}</p>
                    </div>
                    <p className="font-mono text-sm">₹ {(it.price * it.quantity).toLocaleString("en-IN")}</p>
                  </div>
                ))}
              </div>

              <Tracker order={o} />

              {o.return_reason && (
                <div className="mt-4 border-t border-foreground/20 pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your Reason</p>
                  <p className="text-sm italic mt-1">"{o.return_reason}"</p>
                  {o.return_refund_preference && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Refund via: <span className="text-foreground">{o.return_refund_preference.replace(/_/g, " ")}</span>
                    </p>
                  )}
                  {o.return_images?.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {o.return_images.map((src, i) => (
                        <div key={i} className="w-16 h-16 bg-secondary overflow-hidden">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {o.vendor_return_notes && (
                <div className="mt-3 border-t border-foreground/20 pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendor Notes</p>
                  <p className="text-sm mt-1">{o.vendor_return_notes}</p>
                </div>
              )}
              {o.vendor_dispute?.reason && (
                <div className="mt-3 border-t border-destructive/40 pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Vendor Dispute</p>
                  <p className="text-sm mt-1">{o.vendor_dispute.reason}</p>
                </div>
              )}
              {o.admin_return_notes && (
                <div className="mt-3 border-t border-foreground/20 pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Spotters Hub Verdict</p>
                  <p className="text-sm mt-1">{o.admin_return_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ReturnDialog order={returning} open={!!returning} onClose={() => setReturning(null)} onSubmitted={load} />
    </div>
  );
}
